import { useCallback, useReducer } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import
import { LeadDetailInfo } from '@/components/leads/LeadDetailInfo';
import { ServiceRequirementsCard } from '@/components/leads/ServiceRequirementsCard';
import { InsuranceRequestsPanel } from '@/components/leads/InsuranceRequestsPanel';
import { LeadAiDraftPanel } from '@/components/leads/LeadAiDraftPanel';
import { LeadFactsPanel } from '@/components/leads/LeadFactsPanel';
import { LeadEmailHistoryPanel } from '@/components/leads/LeadEmailHistoryPanel';
import { EditCollectedDataDialog } from '@/components/leads/EditCollectedDataDialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import type { IPolicyFieldValue } from '@/@types/insurance';
import type { IOrgServiceType, ILeadServiceData } from '@/@types/serviceTypes';

type TPageState = {
  // LOCAL_TYPE_JUSTIFIED: page-level reducer state — internal to this component only
  editDialogOpen: boolean;
  editingServiceIndex: number | null;
};

type TPageAction = // LOCAL_TYPE_JUSTIFIED: page-level reducer action — internal to this component only
  { type: 'OPEN_EDIT'; index: number } | { type: 'CLOSE_EDIT' };

function pageReducer(_state: TPageState, action: TPageAction): TPageState {
  switch (action.type) {
    case 'OPEN_EDIT':
      return { editDialogOpen: true, editingServiceIndex: action.index };
    case 'CLOSE_EDIT':
      return { editDialogOpen: false, editingServiceIndex: null };
  }
}

export function LeadDetailPage() {
  const navigate = useNavigate();
  const { leadId } = useParams<{ leadId: string }>();

  const [state, dispatch] = useReducer(pageReducer, {
    editDialogOpen: false,
    editingServiceIndex: null,
  });

  const lead = useQuery(
    api.leads.get,
    leadId ? { leadId: leadId as Id<'leads'> } : 'skip' // AS_JUSTIFIED: one-time Id cast from route param at component top
  );

  const currentOrg = useQuery(api.organisations.queries.getCurrentOrg);

  const serviceTypes = useQuery(
    api.serviceTypes.listByOrg,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );

  const serviceDataList = useQuery(
    api.leadServiceData.listByLead,
    leadId ? { leadId: leadId as Id<'leads'> } : 'skip' // AS_JUSTIFIED: one-time Id cast from route param at component top
  );

  const updateCollectedFields = useMutation(api.leadServiceData.updateCollectedFields);

  const isLoading =
    lead === undefined ||
    currentOrg === undefined ||
    serviceTypes === undefined ||
    serviceDataList === undefined;

  const handleBack = useCallback(() => {
    navigate('/leads');
  }, [navigate]);

  const handleEdit = useCallback((index: number) => {
    dispatch({ type: 'OPEN_EDIT', index });
  }, []);

  const handleSave = useCallback(
    async (fields: IPolicyFieldValue[]) => {
      if (state.editingServiceIndex === null || !serviceDataList) return;
      const entry = serviceDataList[state.editingServiceIndex];
      if (!entry) return;

      await updateCollectedFields({
        serviceDataId: entry._id,
        collectedFields: fields,
      });
    },
    [state.editingServiceIndex, serviceDataList, updateCollectedFields]
  );

  // Build matched pairs of serviceData + serviceType
  const serviceEntries: Array<{ data: ILeadServiceData; type: IOrgServiceType }> = [];
  if (serviceDataList && serviceTypes) {
    const typeMap = new Map<string, IOrgServiceType>();
    for (const t of serviceTypes) {
      typeMap.set(t._id, t as unknown as IOrgServiceType); // AS_JUSTIFIED: Convex useQuery infers implicit type for listByOrg; shape is guaranteed by Convex return validator
    }
    for (const entry of serviceDataList) {
      const type = typeMap.get(entry.serviceTypeId);
      if (type) {
        serviceEntries.push({
          data: entry as unknown as ILeadServiceData, // AS_JUSTIFIED: Convex return matches ILeadServiceData shape; no type guard available for Convex return types
          type,
        });
      }
    }
  }

  // Extract address fields from service data for the left column
  const addressFields = serviceEntries
    .flatMap(entry => entry.data.collectedFields)
    .reduce((acc: { address?: string; city?: string; postalCode?: string }, f) => {
      if (f.fieldLabel === 'Home address' && f.value) acc.address = f.value;
      if (f.fieldLabel === 'City' && f.value) acc.city = f.value;
      if (f.fieldLabel === 'Postal code' && f.value) acc.postalCode = f.value;
      return acc;
    }, {});

  // Website-ingested leads carry their address on the lead doc, not in service data.
  const addressProps = {
    address: addressFields.address ?? lead?.address,
    city: addressFields.city ?? lead?.residence,
    postalCode: addressFields.postalCode,
  };

  const editingEntry =
    state.editingServiceIndex !== null ? serviceEntries[state.editingServiceIndex] : null;

  if (isLoading) {
    return (
      <div className="min-h-full bg-background-secondary p-8">
        <div className="flex items-center gap-3 mb-6">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-7 w-48" />
        </div>
        <div className="flex gap-6">
          <div className="w-[400px] shrink-0 space-y-4">
            <Skeleton className="h-48 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
          <div className="flex-1 space-y-4">
            <Skeleton className="h-64 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="min-h-full bg-background-secondary flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-foreground mb-1">Lead not found</h2>
          <p className="text-sm text-muted-foreground mb-4">
            This lead may have been deleted or you don&apos;t have access.
          </p>
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Leads
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary p-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-xl font-semibold text-foreground">{lead.name ?? 'Unknown Lead'}</h1>
      </div>

      {/* Two-column layout */}
      <div className="flex gap-6">
        {/* Left column — fixed width */}
        <div className="w-[400px] shrink-0">
          <LeadDetailInfo lead={lead} {...addressProps} />
        </div>

        {/* Right column — flexible */}
        <div className="flex-1 space-y-4">
          {/* Website-ingested panels — self-hide when no data */}
          <InsuranceRequestsPanel leadId={lead._id} />
          <LeadAiDraftPanel leadId={lead._id} />

          {serviceEntries.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <p className="text-sm">No services linked to this lead yet.</p>
            </div>
          )}

          {serviceEntries.map((entry, index) => (
            <ServiceRequirementsCard
              key={entry.data._id}
              serviceData={entry.data}
              serviceType={entry.type}
              onEdit={() => handleEdit(index)}
            />
          ))}

          <LeadFactsPanel leadId={lead._id} />
          {/* key remounts the panel per lead so its error boundary state can't leak across leads */}
          <LeadEmailHistoryPanel key={lead._id} leadId={lead._id} />
        </div>
      </div>

      {/* Edit dialog */}
      {editingEntry && (
        <EditCollectedDataDialog
          open={state.editDialogOpen}
          onOpenChange={open => {
            if (!open) dispatch({ type: 'CLOSE_EDIT' });
          }}
          serviceType={editingEntry.type}
          serviceData={editingEntry.data}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
